// src/frontend_react/src/components/auth/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext'; // Correctly using the hook

// Base UI Components
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import Alert from '../ui/Alert/Alert';
import Spinner from '../ui/Spinner/Spinner';
import Typography from '../ui/Typography/Typography';
import Stack from '../ui/Stack/Stack';
import Box from '../ui/Box/Box';
import Tooltip from '../ui/Tooltip/Tooltip';

// Assets
import CipherByNaluriLogo from '../../assets/cipher-by-naluri-logo.svg';

// Local styles
import styles from './Login.module.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isMfaStep, setIsMfaStep] = useState(false); // Controls which form is shown
  // const [userIdForMfa, setUserIdForMfa] = useState(null); // Not strictly needed if backend uses session
  const [submittingUsername, setSubmittingUsername] = useState(''); // To display username in MFA step

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, verifyMfaLogin, isAuthenticated, isLoading: authIsLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const cipherAcronymExplanation = "CIPHER: Compliant • Integrated • Platform for Holistic Encryption & Reconciliation";
  
  // Existing useEffect for navigation after authentication is fine
  useEffect(() => {
    if (!authIsLoading && isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      const isAdmin = user?.roles?.includes('system_admin');

      if (from !== "/" && from !== "/login") {
        if (from.startsWith('/admin') && isAdmin) {
          navigate(from, { replace: true });
        } else if (from.startsWith('/user') && (!isAdmin || user?.roles?.includes('data_linkage_officer') || user?.roles?.some(r => r.startsWith('data_steward:')))) { // Allow DLO/Stewards to user portal
          navigate(from, { replace: true });
        } else { 
          navigate(isAdmin ? '/admin/dashboard/overview' : '/user/dashboard', { replace: true });
        }
      } else {
        navigate(isAdmin ? '/admin/dashboard/overview' : '/user/dashboard', { replace: true });
      }
    }
  }, [authIsLoading, isAuthenticated, navigate, location.state, user]);


  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    setSubmittingUsername(username); // Store username for MFA step display

    const result = await login(username, password); // auth.login from context

    setIsSubmitting(false);
    if (result.success) {
      if (result.mfa_required) {
        setIsMfaStep(true); // Switch to MFA code input UI
        // setUserIdForMfa(result.user?.id); // Optional: if needed by verifyMfaLogin (not in current AuthContext impl)
        setError(''); // Clear previous username/password error
      } else {
        // Navigation is handled by useEffect because isAuthenticated will become true
      }
    } else {
      setError(result.message || 'Login failed. Please check your credentials.');
    }
  };

  // NEW: Handler for MFA code submission
  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || totpCode.length !== 6) return;
    setIsSubmitting(true);
    setError('');

    const result = await verifyMfaLogin(totpCode); // auth.verifyMfaLogin from context

    setIsSubmitting(false);
    if (result.success) {
      // Navigation is handled by useEffect because isAuthenticated will become true
    } else {
      setError(result.message || 'Invalid MFA code. Please try again.');
      setTotpCode(''); // Clear TOTP code field on error
    }
  };

  if (authIsLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        width="100%"
        p="xl"
        bg="background-page"
      >
        <Spinner size="lg" />
        <Typography variant="body1" color="secondary" mt="md">
          Loading authentication status...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      width="100%"
      p="xl"
      bg="background-page"
    >
      <Box
        bg="background-card"
        p="xl"
        borderRadius="xl"
        boxShadow="lg"
        width="100%"
        style={{ maxWidth: '420px' }}
      >
        <Stack spacing="lg" alignItems="center">
          <img src={CipherByNaluriLogo} alt="CIPHER by Naluri" className={styles.logo} />
          <Typography variant="h2" component="h1" fontWeight="semibold" align="center">
            Welcome to 
            <Tooltip 
              content={cipherAcronymExplanation}
              position="top"
              tooltipClassName={styles.brandAcronymTooltip}
            >
              <span style={{cursor: 'help', borderBottom: '1px dotted var(--cipher-color-text-placeholder)'}}>
                CIPHER
              </span>
            </Tooltip>
          </Typography>
          
          {!isMfaStep ? (
            <>
              <Typography variant="body1" color="secondary" align="center" className={styles.tagline}>
                Secure. Seamless. Trusted Data Linkage.
              </Typography>
              {error && ( <Alert variant="error" onDismiss={() => setError('')}>{error}</Alert> )}
              <form onSubmit={handleLoginSubmit} style={{ width: '100%' }}>
                <Stack spacing="lg">
                  <Input
                    label="Username" id="username" type="text" value={username}
                    onChange={(e) => setUsername(e.target.value)} required disabled={isSubmitting}
                    autoComplete="username"
                  />
                  <Input
                    label="Password" id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} required disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                  <Button type="submit" variant="primary" fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
                    {isSubmitting ? 'Logging in...' : 'Login'}
                  </Button>
                </Stack>
              </form>
            </>
          ) : (
            <>
              <Typography variant="h3" align="center" component="h2" mt="md">Enter MFA Code</Typography>
              <Typography color="secondary" align="center">
                Enter the 6-digit code from your authenticator app for user: <strong>{submittingUsername || 'your account'}</strong>.
              </Typography>
              {error && ( <Alert variant="error" onDismiss={() => setError('')}>{error}</Alert> )}
              <form onSubmit={handleMfaSubmit} style={{ width: '100%' }}>
                <Stack spacing="lg">
                  <Input
                    label="Authenticator Code" id="totpCode" type="text" value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g,''))}
                    required disabled={isSubmitting} maxLength={6} placeholder="123456" autoFocus
                    inputMode="numeric" pattern="[0-9]*"
                  />
                  <Button type="submit" variant="primary" fullWidth isLoading={isSubmitting} disabled={isSubmitting || totpCode.length !== 6}>
                    {isSubmitting ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  <Button variant="outline" fullWidth onClick={() => {
                      setIsMfaStep(false); 
                      setError(''); 
                      // setPassword(''); // Optional: clear password if going back
                      setTotpCode('');
                    }} 
                    disabled={isSubmitting}
                  >
                    Back to Password Login
                  </Button>
                </Stack>
              </form>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default Login;